import React, { useState, useEffect } from 'react';
import { 
    Drawer, Box, Typography, IconButton, Divider, 
    Avatar, Grid, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Skeleton,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CodeIcon from '@mui/icons-material/Code';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import BlockIcon from '@mui/icons-material/Block';
import RuleIcon from '@mui/icons-material/Rule';
import { OtaService } from '../api/services/otaService';
import { RoomService } from '../api/services/roomService';
import { RateService } from '../api/services/rateService';
import { toast } from '../utils/toast';
import { useCurrency } from '../utils/currency';

export default function OTAChannelDetailsDrawer({ open, channelId, onClose, onDisconnect }) {
    const { currencySymbol } = useCurrency();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    
    // --- Disconnect States ---
    const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);

    // --- Sync State ---
    const [isSyncing, setIsSyncing] = useState(false);

    // --- Edit Room Mappings States ---
    const [isEditingRooms, setIsEditingRooms] = useState(false);
    const [editRoomMappings, setEditRoomMappings] = useState([]);
    const [isSavingRooms, setIsSavingRooms] = useState(false);

    // --- Edit Rate Mappings States ---
    const [isEditingRates, setIsEditingRates] = useState(false);
    const [editRateMappings, setEditRateMappings] = useState([]);
    const [isSavingRates, setIsSavingRates] = useState(false);

    // --- Stop-Sell States ---
    const [stopSellModalOpen, setStopSellModalOpen] = useState(false);
    const [stopSellForm, setStopSellForm] = useState({ roomTypeId: '', startDate: '', endDate: '', stopSell: true });
    const [isSubmittingStopSell, setIsSubmittingStopSell] = useState(false);

    // --- Booking Restrictions States ---
    const [restrictionsModalOpen, setRestrictionsModalOpen] = useState(false);
    const [restrictionsForm, setRestrictionsForm] = useState({ roomTypeId: '', startDate: '', endDate: '', minStay: 0, maxStay: 0, closedToArrival: false, closedToDeparture: false });
    const [isSubmittingRestrictions, setIsSubmittingRestrictions] = useState(false);

    // --- Dynamic Lookups ---
    const [roomTypes, setRoomTypes] = useState([]);
    const [ratePlans, setRatePlans] = useState([]);

    // --- Reset Forms on Close ---
    useEffect(() => {
        if (!stopSellModalOpen) {
            setStopSellForm({ roomTypeId: '', startDate: '', endDate: '', stopSell: true });
        }
    }, [stopSellModalOpen]);

    useEffect(() => {
        if (!restrictionsModalOpen) {
            setRestrictionsForm({ roomTypeId: '', startDate: '', endDate: '', minStay: 0, maxStay: 0, closedToArrival: false, closedToDeparture: false });
        }
    }, [restrictionsModalOpen]);

    useEffect(() => {
        const fetchLookups = async () => {
            try {
                const [roomsData, ratesData] = await Promise.all([
                    RoomService.getRoomTypes(),
                    RateService.getRatePlans()
                ]);
                setRoomTypes(Array.isArray(roomsData) ? roomsData : roomsData?.data || []);
                setRatePlans(Array.isArray(ratesData) ? ratesData : ratesData?.data || []);
            } catch (err) {
                console.error("Failed to fetch lookups", err);
            }
        };
        fetchLookups();
    }, []);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const data = await OtaService.getChannelDetails(channelId);
            setDetails(data);
        } catch (error) {
            console.error('Failed to load channel details:', error);
            onClose(); 
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && channelId) {
            fetchDetails();
        } else {
            setDetails(null);
            setDisconnectModalOpen(false);
            setIsEditingRooms(false);
            setIsEditingRates(false);
        }
    }, [open, channelId]);

    const maskString = (str) => {
        if (!str) return 'N/A';
        if (str.length <= 4) return '****';
        return str.substring(0, 4) + '****' + str.substring(str.length - 2);
    };

    const handleDisconnect = async () => {
        setIsDisconnecting(true);
        try {
            await OtaService.disconnectChannel(channelId);
            toast.success("OTA channel integration disconnected successfully");
            setDisconnectModalOpen(false);
            onClose(); // Close drawer
            if (onDisconnect) onDisconnect(); // Refresh parent grid
        } catch (error) {
            console.error('Failed to disconnect channel:', error);
        } finally {
            setIsDisconnecting(false);
        }
    };

    const startEditingRooms = () => {
        setEditRoomMappings(details?.roomMappings ? JSON.parse(JSON.stringify(details.roomMappings)) : []);
        setIsEditingRooms(true);
    };

    const addEditRoomMappingRow = () => {
        setEditRoomMappings(prev => [...prev, { roomTypeId: '', otaRoomCategoryId: '', otaRoomCategoryName: '' }]);
    };

    const removeEditRoomMappingRow = (idx) => {
        setEditRoomMappings(prev => {
            const copy = [...prev];
            copy.splice(idx, 1);
            return copy;
        });
    };

    const handleEditRoomMappingChange = (idx, field, value) => {
        setEditRoomMappings(prev => {
            const copy = [...prev];
            copy[idx][field] = value;
            return copy;
        });
    };

    const saveRoomMappings = async () => {
        setIsSavingRooms(true);
        try {
            await OtaService.updateRoomMapping(channelId, { roomMappings: editRoomMappings });
            toast.success("Channel room type mappings synchronized successfully");
            setIsEditingRooms(false);
            await fetchDetails();
        } catch (error) {
            console.error('Failed to save room mappings:', error);
            if (error.response?.status === 403 || error.response?.status === 405) {
                toast.error('Action not allowed while the hotel is suspended.');
            }
        } finally {
            setIsSavingRooms(false);
        }
    };

    const startEditingRates = () => {
        setEditRateMappings(details?.rateMappings ? JSON.parse(JSON.stringify(details.rateMappings)) : []);
        setIsEditingRates(true);
    };

    const addEditRateMappingRow = () => {
        setEditRateMappings(prev => [...prev, { roomTypeId: '', ratePlanId: '', markupType: 'fixed', markupValue: 0 }]);
    };

    const removeEditRateMappingRow = (idx) => {
        setEditRateMappings(prev => {
            const copy = [...prev];
            copy.splice(idx, 1);
            return copy;
        });
    };

    const handleEditRateMappingChange = (idx, field, value) => {
        setEditRateMappings(prev => {
            const copy = [...prev];
            copy[idx][field] = value;
            return copy;
        });
    };

    const saveRateMappings = async () => {
        setIsSavingRates(true);
        try {
            // Convert markupValue to number before sending
            const sanitizedMappings = editRateMappings.map(rm => ({
                ...rm,
                markupValue: Number(rm.markupValue)
            }));
            await OtaService.updateRateMapping(channelId, { rateMappings: sanitizedMappings });
            toast.success("Channel pricing rate mappings synchronized successfully");
            setIsEditingRates(false);
            await fetchDetails();
        } catch (error) {
            console.error('Failed to save rate mappings:', error);
            if (error.response?.status === 403 || error.response?.status === 405) {
                toast.error('Action not allowed while the hotel is suspended.');
            }
        } finally {
            setIsSavingRates(false);
        }
    };

    const handleStopSellSubmit = async () => {
        setIsSubmittingStopSell(true);
        try {
            const payload = {
                roomTypeId: stopSellForm.roomTypeId,
                startDate: new Date(stopSellForm.startDate).toISOString(),
                endDate: new Date(stopSellForm.endDate).toISOString(),
                stopSell: stopSellForm.stopSell
            };
            await OtaService.manageStopSell(channelId, payload);
            toast.success("Stop-sell restriction rule synchronized successfully for this channel");
            setStopSellModalOpen(false);
            setStopSellForm({ roomTypeId: '', startDate: '', endDate: '', stopSell: true });
            await fetchDetails();
        } catch (error) {
            console.error('Failed to sync stop-sell:', error);
            if (error.response?.status === 403 || error.response?.status === 405) {
                toast.error('Action not allowed while the hotel is suspended.');
            }
        } finally {
            setIsSubmittingStopSell(false);
        }
    };

    const handleRestrictionsSubmit = async () => {
        setIsSubmittingRestrictions(true);
        try {
            const payload = {
                roomTypeId: restrictionsForm.roomTypeId,
                startDate: new Date(restrictionsForm.startDate).toISOString(),
                endDate: new Date(restrictionsForm.endDate).toISOString(),
                minStay: Number(restrictionsForm.minStay),
                maxStay: Number(restrictionsForm.maxStay),
                closedToArrival: restrictionsForm.closedToArrival,
                closedToDeparture: restrictionsForm.closedToDeparture
            };
            await OtaService.setBookingRestrictions(channelId, payload);
            toast.success("Booking restriction policies synchronized successfully for this channel");
            setRestrictionsModalOpen(false);
            setRestrictionsForm({ roomTypeId: '', startDate: '', endDate: '', minStay: 0, maxStay: 0, closedToArrival: false, closedToDeparture: false });
            await fetchDetails();
        } catch (error) {
            console.error('Failed to sync restrictions:', error);
            if (error.response?.status === 403 || error.response?.status === 405) {
                toast.error('Action not allowed while the hotel is suspended.');
            }
        } finally {
            setIsSubmittingRestrictions(false);
        }
    };

    return (
        <Drawer 
            anchor="right" 
            open={open} 
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 500, md: 600 } } }}
        >
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>

            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                <Typography variant="h6" fontWeight="bold">OTA Channel Profile</Typography>
                <IconButton onClick={onClose} color="inherit">
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
                {loading ? (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                            <Skeleton variant="circular" width={64} height={64} />
                            <Box>
                                <Skeleton variant="text" width={200} height={40} />
                                <Skeleton variant="text" width={100} height={20} />
                            </Box>
                        </Box>
                        <Skeleton variant="rectangular" height={100} sx={{ mb: 4, borderRadius: 2 }} />
                        <Skeleton variant="text" width={250} height={30} sx={{ mb: 2 }} />
                        <Skeleton variant="rectangular" height={150} sx={{ mb: 4, borderRadius: 2 }} />
                        <Skeleton variant="text" width={250} height={30} sx={{ mb: 2 }} />
                        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
                    </Box>
                ) : details ? (
                    <Box>
                        {/* Header: Platform Name & Logo */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Paper elevation={2} sx={{ p: 1, borderRadius: 3, display: 'flex' }}>
                                    <Avatar 
                                        src={details.logo} 
                                        alt={details.name} 
                                        sx={{ width: 64, height: 64, bgcolor: 'transparent' }}
                                        variant="rounded"
                                    >
                                        {details.name?.charAt(0)}
                                    </Avatar>
                                </Paper>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{details.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">ID: {details.id || channelId}</Typography>
                                </Box>
                            </Box>
                            <Button 
                                variant="outlined" 
                                color="primary" 
                                size="small"
                                disabled={isSyncing}
                                onClick={async () => {
                                    setIsSyncing(true);
                                    try {
                                        await OtaService.syncChannel(channelId);
                                        toast.success("Availability and rates sync triggered successfully for this channel");
                                        await fetchDetails();
                                    } catch (error) {
                                        console.error("Failed to sync channel:", error);
                                    } finally {
                                        setIsSyncing(false);
                                    }
                                }}
                                startIcon={<SyncIcon sx={{ animation: isSyncing ? 'spin 1s linear infinite' : 'none' }} />}
                            >
                                {isSyncing ? 'Syncing...' : 'Force Inventory Push'}
                            </Button>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* API Configuration */}
                        <Typography variant="h6" fontWeight="bold" gutterBottom>API Configuration</Typography>
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={6}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <VpnKeyIcon color="action" fontSize="small" />
                                        <Typography variant="body2" color="text.secondary">API Key</Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                                        {maskString(details.config?.apiKey)}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <CodeIcon color="action" fontSize="small" />
                                        <Typography variant="body2" color="text.secondary">Channel Code</Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                                        {details.config?.channelCode || 'N/A'}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Inventory Sync Mappings: Room Mappings */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold">Room Mappings</Typography>
                            {!isEditingRooms && (
                                <Button 
                                    size="small" 
                                    startIcon={<EditIcon />} 
                                    onClick={startEditingRooms}
                                >
                                    Edit Room Mappings
                                </Button>
                            )}
                        </Box>

                        {isEditingRooms ? (
                            <Box sx={{ mb: 4 }}>
                                {editRoomMappings.map((mapping, idx) => (
                                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} key={idx}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={10} sm={11}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth size="small" disabled={isSavingRooms}>
                                                            <InputLabel>Internal Room Type</InputLabel>
                                                            <Select
                                                                label="Internal Room Type"
                                                                value={mapping.roomTypeId}
                                                                onChange={(e) => handleEditRoomMappingChange(idx, 'roomTypeId', e.target.value)}
                                                            >
                                                                {roomTypes.map((rt) => (
                                                                    <MenuItem key={rt.id} value={rt.id}>{rt.name}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth size="small"
                                                            label="OTA Category ID"
                                                            value={mapping.otaRoomCategoryId}
                                                            onChange={(e) => handleEditRoomMappingChange(idx, 'otaRoomCategoryId', e.target.value)}
                                                            disabled={isSavingRooms}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            fullWidth size="small"
                                                            label="OTA Category Name"
                                                            value={mapping.otaRoomCategoryName}
                                                            onChange={(e) => handleEditRoomMappingChange(idx, 'otaRoomCategoryName', e.target.value)}
                                                            disabled={isSavingRooms}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={2} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <IconButton color="error" onClick={() => removeEditRoomMappingRow(idx)} disabled={isSavingRooms}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                ))}
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Button 
                                        startIcon={<AddIcon />} 
                                        variant="outlined" 
                                        size="small" 
                                        onClick={addEditRoomMappingRow}
                                        disabled={isSavingRooms}
                                    >
                                        Add Mapping Row
                                    </Button>
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Button 
                                        color="inherit" 
                                        onClick={() => setIsEditingRooms(false)}
                                        disabled={isSavingRooms}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        onClick={saveRoomMappings}
                                        disabled={isSavingRooms}
                                    >
                                        {isSavingRooms ? 'Saving...' : 'Save Mappings'}
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <TableContainer component={Paper} sx={{ mb: 4 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Internal Room Type</TableCell>
                                            <TableCell>OTA Category ID</TableCell>
                                            <TableCell>OTA Category Name</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {details.roomMappings?.length > 0 ? (
                                            details.roomMappings.map((rm, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{rm.roomTypeId}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace' }}>{rm.otaRoomCategoryId}</TableCell>
                                                    <TableCell>{rm.otaRoomCategoryName}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>No room mappings found.</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Inventory Sync Mappings: Rate Mappings */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold">Rate Plan Rules</Typography>
                            {!isEditingRates && (
                                <Button 
                                    size="small" 
                                    startIcon={<EditIcon />} 
                                    onClick={startEditingRates}
                                >
                                    Edit Rate Rules
                                </Button>
                            )}
                        </Box>

                        {isEditingRates ? (
                            <Box sx={{ mb: 4 }}>
                                {editRateMappings.map((mapping, idx) => (
                                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} key={idx}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={10} sm={11}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth size="small" disabled={isSavingRates}>
                                                            <InputLabel>Internal Room Type</InputLabel>
                                                            <Select
                                                                label="Internal Room Type"
                                                                value={mapping.roomTypeId}
                                                                onChange={(e) => handleEditRateMappingChange(idx, 'roomTypeId', e.target.value)}
                                                            >
                                                                {roomTypes.map((rt) => (
                                                                    <MenuItem key={rt.id} value={rt.id}>{rt.name}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth size="small" disabled={isSavingRates}>
                                                            <InputLabel>Internal Rate Plan</InputLabel>
                                                            <Select
                                                                label="Internal Rate Plan"
                                                                value={mapping.ratePlanId}
                                                                onChange={(e) => handleEditRateMappingChange(idx, 'ratePlanId', e.target.value)}
                                                            >
                                                                {ratePlans.map((rp) => (
                                                                    <MenuItem key={rp.id} value={rp.id}>{rp.name}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControl fullWidth size="small" disabled={isSavingRates}>
                                                            <InputLabel>Markup Type</InputLabel>
                                                            <Select
                                                                label="Markup Type"
                                                                value={mapping.markupType}
                                                                onChange={(e) => handleEditRateMappingChange(idx, 'markupType', e.target.value)}
                                                            >
                                                                <MenuItem value="fixed">Fixed ({currencySymbol})</MenuItem>
                                                                <MenuItem value="percentage">Percentage (%)</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth size="small"
                                                            label="Markup Value"
                                                            type="number"
                                                            value={mapping.markupValue}
                                                            onChange={(e) => handleEditRateMappingChange(idx, 'markupValue', e.target.value)}
                                                            placeholder="0"
                                                            disabled={isSavingRates}
                                                            inputProps={{ min: 0 }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={2} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <IconButton color="error" onClick={() => removeEditRateMappingRow(idx)} disabled={isSavingRates}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                ))}
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Button 
                                        startIcon={<AddIcon />} 
                                        variant="outlined" 
                                        size="small" 
                                        onClick={addEditRateMappingRow}
                                        disabled={isSavingRates}
                                    >
                                        Add Rate Mapping
                                    </Button>
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Button 
                                        color="inherit" 
                                        onClick={() => setIsEditingRates(false)}
                                        disabled={isSavingRates}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        onClick={saveRateMappings}
                                        disabled={isSavingRates}
                                    >
                                        {isSavingRates ? 'Saving...' : 'Save Mappings'}
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Room Type</TableCell>
                                            <TableCell>Rate Plan</TableCell>
                                            <TableCell>Markup Type</TableCell>
                                            <TableCell>Markup Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {details.rateMappings?.length > 0 ? (
                                            details.rateMappings.map((rm, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{rm.roomTypeId}</TableCell>
                                                    <TableCell>{rm.ratePlanId}</TableCell>
                                                    <TableCell sx={{ textTransform: 'capitalize' }}>{rm.markupType}</TableCell>
                                                    <TableCell>
                                                        {rm.markupType === 'percentage' ? `${rm.markupValue}%` : `${currencySymbol} ${rm.markupValue}`}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No rate mappings found.</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Restrictions Section */}
                        <Box sx={{ mt: 4, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" fontWeight="bold">Channel Restrictions</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button 
                                    variant="outlined" 
                                    color="info" 
                                    startIcon={<RuleIcon />}
                                    onClick={() => setRestrictionsModalOpen(true)}
                                >
                                    Set Booking Restrictions
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    color="warning" 
                                    startIcon={<BlockIcon />}
                                    onClick={() => setStopSellModalOpen(true)}
                                >
                                    Manage Stop-Sell
                                </Button>
                            </Box>
                        </Box>

                        <Paper variant="outlined" sx={{ p: 3, mb: 4, bgcolor: 'rgba(0,0,0,0.01)' }}>
                            <Typography variant="body2" color="text.secondary">
                                Use Stop-Sell or Booking Restrictions to limit availability and enforce stay policies on this OTA channel for specific date ranges. These rules will override standard inventory allocations.
                            </Typography>
                        </Paper>

                        {/* Disconnect Action */}
                        <Box sx={{ mt: 6, mb: 2, p: 3, border: '1px solid', borderColor: 'error.main', borderRadius: 2, bgcolor: 'error.50' }}>
                            <Typography variant="subtitle1" color="error.main" fontWeight="bold" gutterBottom>
                                Danger Zone
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Disconnecting this OTA channel will terminate live room inventory updates and rate plan synchronization immediately. This action cannot be undone.
                            </Typography>
                            <Button 
                                variant="contained" 
                                color="error" 
                                onClick={() => setDisconnectModalOpen(true)}
                                startIcon={<WarningAmberIcon />}
                            >
                                Disconnect Channel
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Typography color="text.secondary" align="center" sx={{ mt: 10 }}>
                        No channel details available.
                    </Typography>
                )}
            </Box>

            {/* Disconnect Confirmation Modal */}
            <Dialog 
                open={disconnectModalOpen} 
                onClose={() => !isDisconnecting && setDisconnectModalOpen(false)}
            >
                <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon /> Disconnect OTA Channel
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        <b>Warning:</b> Are you sure you want to disconnect this OTA channel? This will terminate live room inventory updates and rate plan synchronization immediately. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setDisconnectModalOpen(false)} 
                        disabled={isDisconnecting}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDisconnect} 
                        color="error" 
                        variant="contained"
                        disabled={isDisconnecting}
                        startIcon={isDisconnecting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Stop-Sell Modal */}
            <Dialog 
                open={stopSellModalOpen} 
                onClose={() => !isSubmittingStopSell && setStopSellModalOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BlockIcon color="warning" /> Manage Stop-Sell
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Configure an emergency block on room sales for specific dates on this platform.
                    </Typography>

                    <FormControl fullWidth size="small" sx={{ mb: 3 }} disabled={isSubmittingStopSell}>
                        <InputLabel>Internal Room Type</InputLabel>
                        <Select
                            label="Internal Room Type"
                            value={stopSellForm.roomTypeId}
                            onChange={(e) => setStopSellForm({...stopSellForm, roomTypeId: e.target.value})}
                        >
                            {roomTypes.map((rt) => (
                                <MenuItem key={rt.id} value={rt.id}>{rt.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        size="small"
                        type="datetime-local"
                        label="Start Date & Time"
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 3 }}
                        value={stopSellForm.startDate}
                        onChange={(e) => setStopSellForm({...stopSellForm, startDate: e.target.value})}
                        disabled={isSubmittingStopSell}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        type="datetime-local"
                        label="End Date & Time"
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 3 }}
                        value={stopSellForm.endDate}
                        onChange={(e) => setStopSellForm({...stopSellForm, endDate: e.target.value})}
                        disabled={isSubmittingStopSell}
                    />

                    <FormControlLabel
                        control={
                            <Switch 
                                checked={stopSellForm.stopSell}
                                onChange={(e) => setStopSellForm({...stopSellForm, stopSell: e.target.checked})}
                                color="warning"
                                disabled={isSubmittingStopSell}
                            />
                        }
                        label={<b>{stopSellForm.stopSell ? 'Activate Stop-Sell Block' : 'Lift Stop-Sell Block'}</b>}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setStopSellModalOpen(false)} 
                        disabled={isSubmittingStopSell}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleStopSellSubmit} 
                        color="warning" 
                        variant="contained"
                        disabled={!stopSellForm.roomTypeId || !stopSellForm.startDate || !stopSellForm.endDate || isSubmittingStopSell}
                        startIcon={isSubmittingStopSell ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isSubmittingStopSell ? 'Saving...' : 'Sync Stop-Sell'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Set Booking Restrictions Modal */}
            <Dialog 
                open={restrictionsModalOpen} 
                onClose={() => !isSubmittingRestrictions && setRestrictionsModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RuleIcon color="info" /> Set Booking Restrictions
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Define structural stay conditions (like Minimum Length of Stay, CTA, CTD) for specific dates on this third-party channel.
                    </Typography>

                    <FormControl fullWidth size="small" sx={{ mb: 3 }} disabled={isSubmittingRestrictions}>
                        <InputLabel>Internal Room Type</InputLabel>
                        <Select
                            label="Internal Room Type"
                            value={restrictionsForm.roomTypeId}
                            onChange={(e) => setRestrictionsForm({...restrictionsForm, roomTypeId: e.target.value})}
                        >
                            {roomTypes.map((rt) => (
                                <MenuItem key={rt.id} value={rt.id}>{rt.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="datetime-local"
                                label="Start Date & Time"
                                InputLabelProps={{ shrink: true }}
                                value={restrictionsForm.startDate}
                                onChange={(e) => setRestrictionsForm({...restrictionsForm, startDate: e.target.value})}
                                disabled={isSubmittingRestrictions}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="datetime-local"
                                label="End Date & Time"
                                InputLabelProps={{ shrink: true }}
                                value={restrictionsForm.endDate}
                                onChange={(e) => setRestrictionsForm({...restrictionsForm, endDate: e.target.value})}
                                disabled={isSubmittingRestrictions}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Minimum Length of Stay (Nights)"
                                value={restrictionsForm.minStay}
                                onChange={(e) => setRestrictionsForm({...restrictionsForm, minStay: e.target.value})}
                                disabled={isSubmittingRestrictions}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Maximum Length of Stay (Nights)"
                                value={restrictionsForm.maxStay}
                                onChange={(e) => setRestrictionsForm({...restrictionsForm, maxStay: e.target.value})}
                                disabled={isSubmittingRestrictions}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={restrictionsForm.closedToArrival}
                                    onChange={(e) => setRestrictionsForm({...restrictionsForm, closedToArrival: e.target.checked})}
                                    color="info"
                                    disabled={isSubmittingRestrictions}
                                />
                            }
                            label="Closed to Arrival (CTA) - Block new check-ins on these dates"
                        />
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={restrictionsForm.closedToDeparture}
                                    onChange={(e) => setRestrictionsForm({...restrictionsForm, closedToDeparture: e.target.checked})}
                                    color="info"
                                    disabled={isSubmittingRestrictions}
                                />
                            }
                            label="Closed to Departure (CTD) - Block check-outs on these dates"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setRestrictionsModalOpen(false)} 
                        disabled={isSubmittingRestrictions}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleRestrictionsSubmit} 
                        color="info" 
                        variant="contained"
                        disabled={!restrictionsForm.roomTypeId || !restrictionsForm.startDate || !restrictionsForm.endDate || isSubmittingRestrictions}
                        startIcon={isSubmittingRestrictions ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isSubmittingRestrictions ? 'Saving...' : 'Sync Restrictions'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Drawer>
    );
}
